/**
 * Visualizer Loader Service
 *
 * Handles loading visualizers from:
 * 1. Local bundled files (free tier)
 * 2. Supabase Storage (premium visualizers)
 * 3. User-generated visualizers from Supabase Storage
 *
 * Uses RLS policies on Supabase Storage to enforce tier-based access.
 */

import { SUPABASE_URL, SUPABASE_ANON_KEY, isConfigured } from '../config/config.js';

// Import premium manifest (bundled for local dev, uploaded to storage for prod)
// Vite handles JSON imports natively
import premiumManifest from '../config/premium-manifest.json';

/**
 * Custom error for access denied scenarios
 */
export class AccessDeniedError extends Error {
  constructor(message = 'Upgrade to Pro to access this visualizer') {
    super(message);
    this.name = 'AccessDeniedError';
    this.code = 'ACCESS_DENIED';
  }
}

/**
 * Visualizer Loader Singleton
 */
class VisualizerLoader {
  constructor() {
    this.supabase = null;
    this.premiumManifest = premiumManifest;
    this.userVisualizers = null;
    this.loadedModules = new Map();  // Cache loaded modules
  }

  /**
   * Initialize with Supabase client
   * Must be called after auth service is initialized
   */
  setSupabaseClient(client) {
    this.supabase = client;
  }

  /**
   * Get Supabase client (lazy init from global if not set)
   * Will create an anonymous client if needed to fetch public data
   */
  getSupabaseClient() {
    if (this.supabase) return this.supabase;

    // Try to get from global (set by auth service)
    if (typeof window !== 'undefined' && window.supabase && isConfigured()) {
      this.supabase = window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
    }

    return this.supabase;
  }

  /**
   * Get premium visualizers manifest
   * In development: uses bundled manifest
   * In production: could fetch from storage for freshest data
   */
  getPremiumManifest() {
    // Handle case where manifest might not have loaded
    if (!this.premiumManifest) {
      console.warn('[VisualizerLoader] Premium manifest not loaded');
      return {};
    }
    return this.premiumManifest.visualizers || {};
  }

  /**
   * Fetch custom visualizers from database
   * Returns user's own visualizers AND public visualizers from other users
   */
  async getUserVisualizers() {
    if (this.userVisualizers !== null) {
      return this.userVisualizers;
    }

    // Use getSupabaseClient to ensure we can fetch public visualizers
    const supabase = this.getSupabaseClient();
    if (!supabase) {
      this.userVisualizers = [];
      return [];
    }

    try {
      // Query returns: user's own visualizers + public visualizers (via RLS)
      const { data, error } = await supabase
        .from('user_visualizers')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('[VisualizerLoader] Failed to fetch user visualizers:', error.message);
        this.userVisualizers = [];
        return [];
      }

      this.userVisualizers = data || [];
      return this.userVisualizers;
    } catch (err) {
      console.warn('[VisualizerLoader] Error fetching user visualizers:', err);
      this.userVisualizers = [];
      return [];
    }
  }

  /**
   * Get the current authenticated user's ID
   * Returns null if not authenticated
   */
  async getCurrentUserId() {
    const supabase = this.getSupabaseClient();
    if (!supabase) return null;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id || null;
    } catch {
      return null;
    }
  }

  /**
   * Clear user visualizers cache (call on auth state change)
   */
  clearUserVisualizersCache() {
    this.userVisualizers = null;
  }

  /**
   * Load a visualizer from Supabase Storage
   * @param {string} bucket - Storage bucket name
   * @param {string} path - File path within bucket
   * @returns {Promise<Module>} - ES module
   */
  async loadFromStorage(bucket, path) {
    const supabase = this.getSupabaseClient();
    if (!supabase) {
      throw new Error('Supabase client not available');
    }

    // Check cache first
    const cacheKey = `${bucket}/${path}`;
    if (this.loadedModules.has(cacheKey)) {
      return this.loadedModules.get(cacheKey);
    }

    // Download the file
    const { data, error } = await supabase.storage
      .from(bucket)
      .download(path);

    if (error) {
      // Handle access denied (403) - user doesn't have tier access
      if (error.statusCode === 403 || error.message?.includes('403')) {
        throw new AccessDeniedError();
      }
      const msg = typeof error.message === 'string' ? error.message : JSON.stringify(error);
      throw new Error(`Failed to load visualizer: ${msg}`);
    }

    // Convert blob to text
    const code = await data.text();

    // Security scan: reject code using forbidden APIs
    const FORBIDDEN_APIS = /\b(fetch|XMLHttpRequest|sendBeacon|WebSocket|EventSource|importScripts)\s*\(/;
    if (FORBIDDEN_APIS.test(code)) {
      throw new Error(`Visualizer code contains forbidden network API calls`);
    }

    // Create a blob URL and dynamically import
    const blob = new Blob([code], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);

    try {
      const module = await import(/* @vite-ignore */ url);

      // Cache the module
      this.loadedModules.set(cacheKey, module);

      return module;
    } finally {
      // Clean up blob URL
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Load a premium visualizer by name
   */
  async loadPremiumVisualizer(name) {
    const manifest = this.getPremiumManifest();
    const vizInfo = manifest[name];

    if (!vizInfo) {
      throw new Error(`Premium visualizer not found: ${name}`);
    }

    return this.loadFromStorage('premium-visualizers', vizInfo.filename);
  }

  /**
   * Load a user-generated visualizer
   */
  async loadUserVisualizer(vizId) {
    const userVizs = await this.getUserVisualizers();
    const viz = userVizs.find(v => v.id === vizId || v.name === vizId);

    if (!viz) {
      throw new Error(`User visualizer not found: ${vizId}`);
    }

    return this.loadFromStorage('user-visualizers', viz.storage_path);
  }

  /**
   * Check if premium visualizers should be loaded from storage
   * Returns false in development mode where files are served locally
   */
  shouldUseStorage() {
    // Check for development mode indicators
    if (typeof window !== 'undefined') {
      // Vite dev server (default port 5173 or configured port 3000)
      const devPorts = ['5173', '3000', '3001', '8080'];
      if (devPorts.includes(window.location.port)) return false;
      // localhost without port usually means dev
      if (window.location.hostname === 'localhost') return false;
      // 127.0.0.1 is also dev
      if (window.location.hostname === '127.0.0.1') return false;
    }
    return isConfigured();
  }
}

// Export singleton instance
export const visualizerLoader = new VisualizerLoader();
export default visualizerLoader;
